import { useForm } from "react-hook-form";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SelectClienteFinal } from "@/components/ui/SelectClienteFinal";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useRegisterAprendizWithUsuario } from "@/hooks/aprendices/useRegisterAprendizWithUsuario";
import type { CreateAprendizWithUserInput } from "@/lib/validaciones/createAprendizWithUsuario.validacion";
import { createAprendizWithUserSchema } from "@/lib/validaciones/createAprendizWithUsuario.validacion";
import { Eye, EyeOff, Search, UserCheck, UserPlus } from "lucide-react";
import { useAprendicesNotInPrograma } from "@/hooks/aprendices/useAprendicesNotInPrograma";
import { useRegisterAprendicesPrograma } from "@/hooks/aprendicesProgramas/useAprendicesProgramas";
import { cn } from "@/lib/utils";

export function RegisterAprendizWithUsuarioForm(
  {
    setOpen,
    idPrograma,
    idClienteFinal,
    queryKey = ["aprendices"],
  }: {
    setOpen: (open: boolean) => void;
    idPrograma?: number;
    idClienteFinal?: number;
    queryKey?: (string | number | undefined)[];
  } = {
    setOpen: () => {},
  },
) {
  const { usuarioInfo } = useCurrentUser();
  const isClienteFinal = usuarioInfo?.rol?.nombre === "CLIENTE";
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<"nuevo" | "existente">("nuevo");
  const [existenteSearch, setExistenteSearch] = useState("");
  const [selectedExistente, setSelectedExistente] = useState<{
    id: number;
    nombre: string;
    correo: string;
  } | null>(null);

  const form = useForm<CreateAprendizWithUserInput>({
    resolver: zodResolver(createAprendizWithUserSchema),
    defaultValues: {
      usuario: {
        nombreUsuario: "",
        correo: "",
        contrasena: "",
        nombre: "",
        apellidoPaterno: "",
        apellidoMaterno: "",
        idRol: 4,
      },
      aprendiz: {
        idClienteFinal: idClienteFinal ?? 0,
      },
    },
  });

  const { mutate, isPending } = useRegisterAprendizWithUsuario(
    idPrograma,
    queryKey,
  );

  const queryClient = useQueryClient();

  const { mutate: vincularExistente, isPending: isVinculando } =
    useRegisterAprendicesPrograma();

  const { data: aprendicesDisponibles, isLoading: isLoadingDisponibles } =
    useAprendicesNotInPrograma(idClienteFinal, idPrograma, existenteSearch);

  const onSubmit = (data: CreateAprendizWithUserInput) => {
    mutate(data, {
      onSuccess: () => {
        form.reset();
        // Si luego reabrimos el modal para seleccionar aprendices existentes,
        // debemos refrescar la lista "not-in-programa" para que no aparezca
        // el nuevo aprendiz ya inscrito.
        if (idPrograma && idClienteFinal) {
          queryClient.invalidateQueries({
            queryKey: ["aprendices-not-in-programa", idClienteFinal, idPrograma],
          });
        }
        setOpen(false);
      },
    });
  };

  const onVincular = () => {
    if (!selectedExistente || !idPrograma) return;
    vincularExistente(
      { idAprendiz: selectedExistente.id, idPrograma, progreso: 0 },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey });
          // Refrescar la lista de "aprendices disponibles" en este mismo modal.
          // (useAprendicesNotInPrograma usa queryKey: ["aprendices-not-in-programa", idClienteFinal, idPrograma, search])
          if (idPrograma && idClienteFinal) {
            queryClient.invalidateQueries({
              queryKey: ["aprendices-not-in-programa", idClienteFinal, idPrograma],
            });
          }
          setSelectedExistente(null);
          setOpen(false);
        },
      },
    );
  };

  return (
    <div className="flex justify-center items-center px-2 sm:px-4">
      <div className="w-full space-y-5">
        {/* Toggle de modo — solo tiene sentido cuando hay idPrograma e idClienteFinal */}
        {idPrograma && idClienteFinal && (
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => {
                setMode("nuevo");
                setSelectedExistente(null);
              }}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors",
                mode === "nuevo"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted",
              )}
            >
              <UserPlus className="h-4 w-4" />
              Nuevo aprendiz
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("existente");
              }}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors",
                mode === "existente"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted",
              )}
            >
              <UserCheck className="h-4 w-4" />
              Ya está registrado
            </button>
          </div>
        )}

        {/* Modo existente */}
        {mode === "existente" && idPrograma && idClienteFinal ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Busca un aprendiz ya registrado en el sistema y agrégalo
              directamente al programa.
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, correo o usuario..."
                value={existenteSearch}
                onChange={(e) => {
                  setExistenteSearch(e.target.value);
                  setSelectedExistente(null);
                }}
                className="pl-9"
              />
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border">
              {isLoadingDisponibles ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Cargando...
                </p>
              ) : !aprendicesDisponibles?.length ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No se encontraron aprendices disponibles
                </p>
              ) : (
                aprendicesDisponibles.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelectedExistente(a)}
                    className={cn(
                      "w-full text-left px-4 py-3 text-sm transition-colors hover:bg-muted",
                      selectedExistente?.id === a.id &&
                        "bg-primary/10 font-medium",
                    )}
                  >
                    <span className="block">{a.nombre}</span>
                    <span className="block text-xs text-muted-foreground">
                      {a.correo}
                    </span>
                  </button>
                ))
              )}
            </div>
            {selectedExistente && (
              <div className="rounded-lg bg-primary/10 border border-primary/20 px-4 py-3 text-sm">
                <p className="font-medium">
                  Seleccionado: {selectedExistente.nombre}
                </p>
                <p className="text-muted-foreground text-xs">
                  {selectedExistente.correo}
                </p>
              </div>
            )}
            <Button
              type="button"
              disabled={!selectedExistente || isVinculando}
              onClick={onVincular}
              className="w-full h-10"
            >
              {isVinculando ? "Agregando..." : "Agregar al programa"}
            </Button>
          </div>
        ) : (
          /* Modo nuevo */
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 sm:space-y-6 w-full"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {typeof idClienteFinal === "undefined" && (
                  <FormField
                    control={form.control}
                    name="aprendiz.idClienteFinal"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>Cliente final</FormLabel>
                        <FormControl>
                          <SelectClienteFinal
                            value={
                              field.value && field.value !== 0
                                ? { value: field.value, label: "" }
                                : null
                            }
                            onChange={(option) =>
                              field.onChange(option ? option.value : 0)
                            }
                            placeholder="Selecciona un cliente final"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="usuario.nombreUsuario"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Nombre de usuario</FormLabel>
                      <FormControl>
                        <Input placeholder="Nombre de usuario" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="usuario.nombre"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Nombre</FormLabel>
                      <FormControl>
                        <Input placeholder="Nombre" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="usuario.apellidoPaterno"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Apellido paterno</FormLabel>
                      <FormControl>
                        <Input placeholder="Apellido" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="usuario.apellidoMaterno"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Apellido materno</FormLabel>
                      <FormControl>
                        <Input placeholder="Apellido materno" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="usuario.correo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Correo</FormLabel>
                      <FormControl>
                        <Input placeholder="correo@ejemplo.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="usuario.contrasena"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Contraseña</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="*******"
                            className="pr-10"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                            aria-label={
                              showPassword
                                ? "Ocultar contraseña"
                                : "Mostrar contraseña"
                            }
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                disabled={isPending}
                className="w-full h-10 sm:h-11 text-sm sm:text-base"
                style={
                  isClienteFinal && usuarioInfo?.clienteFinal
                    ? {
                        background: `linear-gradient(90deg, ${
                          usuarioInfo.clienteFinal.colorPrimario || "#1e293b"
                        }, ${
                          usuarioInfo.clienteFinal.colorSecundario || "#64748b"
                        })`,
                        border: "none",
                        color: "white",
                      }
                    : undefined
                }
              >
                {isPending ? "Registrando..." : "Registrar Aprendiz"}
              </Button>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
}
